import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchAccount,
  createPortalSession,
  AccountSummary,
  PortalSessionResponse,
} from '../services/reportsApi';
import { CACHE_TIME } from '../constants';
import { shouldRetry } from './useReports';

// Query keys for the account summary. A factory rather than inline arrays, so
// an invalidation and a read cannot drift apart.
export const accountKeys = {
  all: ['account'] as const,
  summary: () => [...accountKeys.all, 'summary'] as const,
};

/**
 * The signed-in user's plan, balance and purchasable catalog.
 *
 * Carries a BOUNDED poll for the window after a checkout completes. The
 * overlay closes before the balance changes: the provider delivers a webhook
 * to the backend out of band, which lands in seconds but is not something the
 * browser is told about. So the page polls.
 *
 * Bounded is the whole point, and this is where it differs from
 * useReportStatus, which polls until a terminal status. A balance has no
 * terminal state -- every value it returns is a legitimate answer -- so the
 * same shape would poll forever on a page left open in a background tab.
 * startBalancePoll opens a time window (CACHE_TIME.BALANCE_POLL_WINDOW_MS)
 * and the poll ends when it closes.
 *
 * The window is a deadline rather than a count of attempts because
 * refetchInterval is not called once per fetch: query-core evaluates it on
 * every render and several times per fetch, so a counter in it ran out after
 * a handful of requests. refetchInterval stays pure and only reads the
 * deadline; a timer ends the poll at the deadline, whether or not a response
 * has landed by then.
 *
 * `enabled` defaults to true. My Reports passes false until it knows the
 * account can change what it shows, so users with reports make no request.
 */
export function useAccount(options: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  // When the current post-checkout window closes, or null when not polling.
  // State rather than a ref so a restart inside an open window re-arms the
  // timer below.
  const [pollUntil, setPollUntil] = useState<number | null>(null);
  const polling = pollUntil !== null;

  const query = useQuery<AccountSummary, Error>({
    queryKey: accountKeys.summary(),
    queryFn: fetchAccount,
    enabled: options.enabled ?? true,
    staleTime: polling ? 0 : CACHE_TIME.STALE_TIME_MS,
    retry: shouldRetry,
    refetchInterval: () =>
      pollUntil !== null && Date.now() < pollUntil ? CACHE_TIME.POLL_INTERVAL_MS : false,
  });

  // Give up quietly when the window closes. The balance is correct on the
  // server either way, and the next page load or manual refresh will show it;
  // a stuck spinner would be a worse answer than a stale number.
  useEffect(() => {
    if (pollUntil === null) {
      return undefined;
    }
    const timer = setTimeout(() => setPollUntil(null), Math.max(0, pollUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [pollUntil]);

  // Call when a checkout completes. Restarts the window so a second purchase
  // in the same session gets a full one rather than whatever the first left.
  const startBalancePoll = useCallback(() => {
    setPollUntil(Date.now() + CACHE_TIME.BALANCE_POLL_WINDOW_MS);
    queryClient.invalidateQueries({ queryKey: accountKeys.summary() });
  }, [queryClient]);

  const stopBalancePoll = useCallback(() => {
    setPollUntil(null);
  }, []);

  return { ...query, polling, startBalancePoll, stopBalancePoll };
}

/**
 * Opens the provider's customer portal.
 *
 * A separate export rather than another field on useAccount's return: the
 * Account component test stubs useAccount wholesale, and widening it there
 * would force every one of those stubs to grow a field they do not use.
 *
 * The caller performs a full-page navigation rather than a router push, since
 * the destination is the provider's own domain. The session token is
 * short-lived, so the URL is fetched on click and never cached.
 */
export function usePortalSession() {
  return useMutation<PortalSessionResponse, Error, void>({
    mutationFn: createPortalSession,
  });
}
